import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as ArrowBackIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Role {
  roleId: string;
  roleName: string;
}

interface Location {
  locationId: string;
  locationName: string;
}

interface JobTag {
  job_tag_id: string;
  tag_name: string;
  description: string;
}

interface Team {
  groupId: string;
  groupName: string;
  description: string;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
  locationId: string | null;
  address: string;
}

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [showJobTagsDialog, setShowJobTagsDialog] = useState(false);
  const [showTeamsDialog, setShowTeamsDialog] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    roleId: '',
    locationId: null,
    address: '',
  });

  const [selectedJobTags, setSelectedJobTags] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/');
          return;
        }

        // Fetch all required data
        const [rolesResponse, locationsResponse, jobTagsResponse, teamsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/roles`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/locations`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/job-tags`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}/teams`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        const filteredRoles = rolesResponse.data.filter((role: Role) => role.roleName !== 'Super Admin');
        // Deduplicate roles by roleName - keep only the first occurrence of each role name
        const uniqueRolesMap = new Map<string, Role>();
        filteredRoles.forEach((role: Role) => {
          if (!uniqueRolesMap.has(role.roleName)) {
            uniqueRolesMap.set(role.roleName, role);
          }
        });
        setRoles(Array.from(uniqueRolesMap.values()));
        setLocations(locationsResponse.data);
        setJobTags(jobTagsResponse.data);
        setTeams(teamsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
      }
    };

    fetchData();
  }, [navigate]);

  // Add validation function
  const validateAddress = (address: string): { isValid: boolean; message: string } => {
    if (!address) {
      return { isValid: true, message: '' }; // Address is optional
    }

    // Check minimum length
    if (address.length < 5) {
      return { isValid: false, message: 'Address is too short' };
    }

    // Check maximum length
    if (address.length > 200) {
      return { isValid: false, message: 'Address is too long' };
    }

    // Check for basic address components
    const hasNumber = /\d/.test(address);
    const hasPostalCode = /\b[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}\b/i.test(address); // UK postcode format

    if (!hasNumber) {
      return { isValid: false, message: 'Address should include a number' };
    }

    if (!hasPostalCode) {
      return { isValid: false, message: 'Address should include a postal code' };
    }

    return { isValid: true, message: '' };
  };

  // Update handleInputChange to include address validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      
      // If address changes, validate it
      if (name === 'address') {
        const validation = validateAddress(value);
        if (!validation.isValid) {
          setError(validation.message);
        } else {
          setError('');
        }
      }
      
      return newData;
    });
  };

  // Update handleSubmit to include address validation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!formData.roleId) {
      setError('Please select a role');
      setLoading(false);
      return;
    }

    // Validate address before submission
    const addressValidation = validateAddress(formData.address);
    if (!addressValidation.isValid) {
      setError(addressValidation.message);
      setLoading(false);
      return;
    }

    try {
      // Find the role name from the roleId
      const selectedRole = roles.find(role => role.roleId === formData.roleId);
      if (!selectedRole) {
        setError('Invalid role selected');
        setLoading(false);
        return;
      }

      // First create the user with a temporary password
      const userResponse = await axios.post(
        `${API_BASE_URL}/users`,
        {
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          role: selectedRole.roleName,
          locationId: formData.locationId,
          address: formData.address,
          password: 'TemporaryPassword123!'
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Check if user was created successfully (backend returns userId in the response)
      if (userResponse.data.userId) {
        const userId = userResponse.data.userId;
        let successMessage = `User ${formData.firstName} ${formData.lastName} created successfully`;

        // Add job tags if selected
        if (selectedJobTags.length > 0) {
          try {
            for (const jobTagId of selectedJobTags) {
              await axios.post(
                `${API_BASE_URL}/job-tags/${jobTagId}/users`,
                { userId },
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
            }
            successMessage += ` with ${selectedJobTags.length} tag(s)`;
          } catch (jobTagError) {
            console.error('Error adding job tags:', jobTagError);
            setError('User created but failed to add tags. Please try adding them again.');
          }
        }

        // Add team memberships if selected
        if (selectedTeams.length > 0) {
          try {
            for (const teamId of selectedTeams) {
              await axios.post(
                `${API_BASE_URL}/teams/${teamId}/members`,
                { userId },
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
            }
            successMessage += ` and added to ${selectedTeams.length} team(s)`;
          } catch (teamError) {
            console.error('Error adding to teams:', teamError);
            setError('User created but failed to add to teams. Please try adding them again.');
          }
        }

        // Send invitation after user is created and associations are made
        try {
          const invitationResponse = await axios.post(
            `${API_BASE_URL}/invitations/send`,
            {
              email: formData.email,
              firstName: formData.firstName,
              lastName: formData.lastName
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );

          if (invitationResponse.data.success) {
            successMessage += `. Invitation sent to ${formData.email}`;
            setSuccess(successMessage);
            setFormData({
              email: '',
              firstName: '',
              lastName: '',
              roleId: '',
              locationId: null,
              address: ''
            });
            setSelectedJobTags([]);
            setSelectedTeams([]);
          }
        } catch (invitationError: any) {
          console.error('Error sending invitation:', invitationError);
          setError('User created but failed to send invitation. Please try sending the invitation again.');
        }
      } else {
        setError('Failed to create user. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to create user. Please check your input and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() => navigate('/admin/users')}
          color="primary"
          sx={{ mr: 2 }}
          aria-label="back to user management"
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Create New User
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Add a new user to your organization
          </Typography>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Main Form Section */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                  Basic Information
                </Typography>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      size="medium"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      variant="outlined"
                      size="medium"
                      helperText="User will receive an invitation email to set their password"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Role</InputLabel>
                      <Select
                        name="roleId"
                        value={formData.roleId || ''}
                        onChange={handleInputChange}
                        label="Role"
                        variant="outlined"
                        size="medium"
                      >
                        {roles.map((role) => (
                          <MenuItem key={role.roleId} value={role.roleId}>
                            {role.roleName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Location</InputLabel>
                      <Select
                        name="locationId"
                        value={formData.locationId || ''}
                        onChange={handleInputChange}
                        label="Location"
                        variant="outlined"
                        size="medium"
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {locations.map((location) => (
                          <MenuItem key={location.locationId} value={location.locationId}>
                            {location.locationName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                      placeholder="Enter full address including street number, street name, city, and postal code"
                      error={!!error && error.includes('Address')}
                      helperText={error && error.includes('Address') ? error : 'Enter the complete address including postal code'}
                      variant="outlined"
                      size="medium"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Tags and Teams Section - Side by Side */}
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Tags Section */}
              <Grid item xs={12} sm={6}>
                <Card elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                      Tags
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setShowJobTagsDialog(true)}
                      color={selectedJobTags.length > 0 ? "success" : "primary"}
                      size="small"
                      sx={{ mb: 1.5 }}
                    >
                      {selectedJobTags.length > 0 ? "Edit Tags" : "Add Tags"}
                    </Button>
                    {selectedJobTags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedJobTags.map(jobTagId => {
                          const tag = jobTags.find(t => t.job_tag_id === jobTagId);
                          return (
                            <Chip 
                              key={jobTagId} 
                              label={tag?.tag_name} 
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Teams Section */}
              <Grid item xs={12} sm={6}>
                <Card elevation={1} sx={{ border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                      Team Memberships
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setShowTeamsDialog(true)}
                      color={selectedTeams.length > 0 ? "success" : "primary"}
                      size="small"
                      sx={{ mb: 1.5 }}
                    >
                      {selectedTeams.length > 0 ? "Edit Teams" : "Add to Teams"}
                    </Button>
                    {selectedTeams.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selectedTeams.map(teamId => {
                          const team = teams.find(t => t.groupId === teamId);
                          return (
                            <Chip 
                              key={teamId} 
                              label={team?.groupName} 
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}
                sx={{ 
                  px: 4, 
                  py: 1.5, 
                  fontSize: '1.1rem',
                  borderRadius: 2
                }}
              >
                {loading ? 'Creating User...' : 'Create User'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Tags Dialog */}
      <Dialog
        open={showJobTagsDialog}
        onClose={() => setShowJobTagsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Tags</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Tags</InputLabel>
                <Select
                  multiple
                  value={selectedJobTags}
                  onChange={(e) => {
                    setSelectedJobTags(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                  }}
                  label="Select Tags"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const tag = jobTags.find(t => t.job_tag_id === value);
                        return (
                          <Chip key={value} label={tag?.tag_name} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {jobTags.map((tag) => (
                    <MenuItem key={tag.job_tag_id} value={tag.job_tag_id}>
                      {tag.tag_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowJobTagsDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setShowJobTagsDialog(false);
            }} 
            variant="contained"
          >
            Save Tags
          </Button>
        </DialogActions>
      </Dialog>

      {/* Teams Dialog */}
      <Dialog
        open={showTeamsDialog}
        onClose={() => setShowTeamsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add to Teams</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Teams</InputLabel>
                <Select
                  multiple
                  value={selectedTeams}
                  onChange={(e) => {
                    setSelectedTeams(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value);
                  }}
                  label="Select Teams"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const team = teams.find(t => t.groupId === value);
                        return (
                          <Chip key={value} label={team?.groupName} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teams.map((team) => (
                    <MenuItem key={team.groupId} value={team.groupId}>
                      {team.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTeamsDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              setShowTeamsDialog(false);
            }} 
            variant="contained"
          >
            Save Teams
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateUser; 